import type { Vec3 } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { beamSolid, boxSolid, planPoint } from '../solids';
import { readNumber } from './params';
import { meshFrameOf, planBounds, planFrameOf } from './planFrame';

const DEG = Math.PI / 180;

/**
 * Harbour crane on a pile raft (PLAN amendment 2026-09-26, painting crop of the IJ front):
 * two splayed shear legs meeting at the head, a short jib rising from the head out over the
 * water with its rope, and a back stay down to the raft. The legs stand across the plan's
 * u axis and the stay runs to -v. On the painting the legs meet at about 0.74 of the mast
 * height (500 of 720 px) and the head leans up and to the painter's right; swinging the jib
 * 60 degrees from +v toward -u reproduces that from the painter's bearing.
 */
export const CRANE = {
  height: 28.3,
  deckY: 0.4,
  deckBottom: -1.1,
  deckInset: 0.5,
  legShare: 0.78,
  legHalf: 0.22,
  headShare: 0.74,
  braceShare: 0.35,
  braceHalf: 0.14,
  jibYaw: 60 * DEG,
  jibOverhang: 0.3,
  jibHalf: 0.18,
  stayShare: 0.85,
  stayHalf: 0.14,
  ropeHalf: 0.05,
  ropeDrop: 7,
  pileHalf: 0.2,
  pileRise: 0.8,
  pileFoot: -1.4,
} as const;

export interface CraneColors {
  readonly timber: LinearRGB;
  readonly rope: LinearRGB;
}

interface CraneSize {
  readonly halfU: number;
  readonly halfV: number;
  readonly height: number;
}

function raft(builder: MeshBuilder, timber: VertexStyle, frame: Frame, size: CraneSize): void {
  const hu = size.halfU - CRANE.deckInset;
  const hv = size.halfV - CRANE.deckInset;
  boxSolid(builder, timber, frame, [-hu, CRANE.deckBottom, -hv], [hu, CRANE.deckY, hv]);
  const pu = hu - CRANE.pileHalf;
  const pv = hv - CRANE.pileHalf;
  for (const [su, sv] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ] as const) {
    const foot = planPoint(su * pu, CRANE.pileFoot, sv * pv);
    const top = planPoint(su * pu, CRANE.deckY + CRANE.pileRise, sv * pv);
    beamSolid(builder, timber, frame, foot, top, CRANE.pileHalf);
  }
}

function frameMembers(builder: MeshBuilder, timber: VertexStyle, frame: Frame, size: CraneSize): Vec3 {
  const spread = CRANE.legShare * (size.halfU - CRANE.deckInset);
  const headY = CRANE.headShare * size.height;
  const head = planPoint(0, headY, 0);
  for (const side of [-1, 1]) {
    beamSolid(builder, timber, frame, planPoint(side * spread, CRANE.deckY, 0), head, CRANE.legHalf);
  }
  const braceY = CRANE.deckY + CRANE.braceShare * (headY - CRANE.deckY);
  const reach = spread * (1 - CRANE.braceShare);
  beamSolid(builder, timber, frame, planPoint(-reach, braceY, 0), planPoint(reach, braceY, 0), CRANE.braceHalf);
  const stayFoot = planPoint(0, CRANE.deckY, -CRANE.stayShare * (size.halfV - CRANE.deckInset));
  beamSolid(builder, timber, frame, planPoint(0, headY, 0), stayFoot, CRANE.stayHalf);
  return head;
}

/** Tip just past the deck edge, so the rope hangs over the water; its top corner reaches the mast height. */
function jibTip(size: CraneSize, head: Vec3): { readonly u: number; readonly v: number; readonly y: number } {
  const du = -Math.sin(CRANE.jibYaw);
  const dv = Math.cos(CRANE.jibYaw);
  const deckExit = Math.min((size.halfU - CRANE.deckInset) / Math.abs(du), (size.halfV - CRANE.deckInset) / Math.abs(dv));
  const reach = deckExit + CRANE.jibOverhang;
  const level = reach / Math.hypot(reach, size.height - head[1]);
  return { u: du * reach, v: dv * reach, y: size.height - level * CRANE.jibHalf };
}

function jibAndRope(builder: MeshBuilder, styles: { timber: VertexStyle; rope: VertexStyle }, frame: Frame, size: CraneSize, head: Vec3): void {
  const tip = jibTip(size, head);
  beamSolid(builder, styles.timber, frame, head, planPoint(tip.u, tip.y, tip.v), CRANE.jibHalf);
  const ropeTop = planPoint(tip.u, tip.y - CRANE.jibHalf, tip.v);
  const ropeFoot = planPoint(tip.u, tip.y - CRANE.ropeDrop, tip.v);
  beamSolid(builder, styles.rope, frame, ropeTop, ropeFoot, CRANE.ropeHalf);
}

export function buildCrane(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: CraneColors,
  edgeId: number,
  seed: number,
  warn: (msg: string) => void
): number {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const b = planBounds(plan);
  const size: CraneSize = {
    halfU: Math.min(-b.u0, b.u1),
    halfV: Math.min(-b.v0, b.v1),
    height: readNumber(landmark.params, 'height', CRANE.height, warn),
  };
  const frame = meshFrameOf(plan);
  const base = { edgeId, seed, strokeAngle: 0, extent: [1, size.height] as const };
  const timber: VertexStyle = { ...base, color: colors.timber, surface: SURFACE.wood };
  const rope: VertexStyle = { ...base, color: colors.rope, surface: SURFACE.linework };
  const before = builder.triangleCount;
  raft(builder, timber, frame, size);
  const head = frameMembers(builder, timber, frame, size);
  jibAndRope(builder, { timber, rope }, frame, size, head);
  return builder.triangleCount - before;
}
